import React, { createRef, useEffect, useState } from "react";

import { NavigationSplitViewOnTileClickedProps } from "models";

import { Flex, Icon } from "components";

export type Preference = {
  name: string;
  value: string;
  position: NavigationSplitViewOnTileClickedProps;
  onClick: (position: NavigationSplitViewOnTileClickedProps) => void;
};

const isElementFullyInView = (element: HTMLElement, container: HTMLElement): boolean => {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return (
    elementRect.left >= containerRect.left &&
    elementRect.right <= containerRect.right &&
    elementRect.top >= containerRect.top &&
    elementRect.bottom <= containerRect.bottom
  );
};

type PreferencesProps = {
  title: string;
  preferences: Preference[];
};
export const Preferences = ({ title, preferences }: PreferencesProps) => {
  const [pillRefs, setPillRefs] = useState<React.MutableRefObject<HTMLDivElement | null>[]>([]);
  const [showIcons, setShowIcons] = useState<boolean>(true);

  const getPillViewDict = () => {
    const pillViewDict: Array<{ index: number; ref: React.MutableRefObject<HTMLDivElement | null>; isInView: boolean }> = [];
    const pillContainer = document.getElementById("pill-container");

    pillRefs.forEach((ref, index) => {
      if (!ref.current) return;
      const isInView = isElementFullyInView(ref?.current, pillContainer!);
      pillViewDict.push({ index, ref, isInView });
    });

    return pillViewDict;
  };

  const onPreviousClicked = () => {
    const pillViewDict = getPillViewDict();
    const firstVisiblePill = pillViewDict.find((pillRef) => pillRef.isInView);

    if (firstVisiblePill && firstVisiblePill.index > 0) {
      pillRefs[firstVisiblePill.index - 1].current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const onNextClicked = () => {
    const pillViewDict = getPillViewDict();
    const firstVisiblePill = pillViewDict.reverse().find((pillRef) => pillRef.isInView);

    if (firstVisiblePill && firstVisiblePill.index < pillViewDict.length - 1) {
      pillRefs[firstVisiblePill.index + 1].current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const onPillClicked = (index: number) => {
    pillRefs[index].current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const handleResize = () => {
    const anyPillHidden = getPillViewDict().some((item) => !item.isInView);
    setShowIcons(anyPillHidden);
  };

  window.addEventListener("resize", handleResize);

  useEffect(() => {
    setPillRefs((elRefs) =>
      Array(preferences.length)
        .fill(null)
        .map((_, i) => elRefs[i] || createRef())
    );
  }, [preferences]);

  useEffect(
    () => {
      if (pillRefs.length > 0) handleResize();

      return () => window.removeEventListener("resize", handleResize);
    },
    [pillRefs.length] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Flex className="preferences container" data-testid="digital-agronomist-preferences" gap="1rem" align="center" justify="space-between">
      <Flex className="preferences prefix" gap="1rem">
        <h5>{title}</h5>
        {showIcons && <Icon iconName="tabler/chevron-left" onClick={onPreviousClicked} />}
      </Flex>

      <Flex className={`preferences pill-container ${!showIcons && "hide-borders"}`} id="pill-container" gap="1rem" wrap="nowrap">
        {preferences.map((preference, index) => (
          <Flex
            className="preferences pill"
            ref={pillRefs[index]}
            key={`${preference.position.groupIndex}-${preference.position.subGroupIndex}-${preference.position.tileIndex}`}
            gap="1rem"
            data-testid={`digital-agronomist-preference-${preference.position.groupIndex}-${preference.position.subGroupIndex}-${preference.position.tileIndex}`}
            onClick={() => {
              onPillClicked(index);
              preference.onClick(preference.position);
            }}
          >
            <p className="preferences name">{preference.name}</p>
            <p className="preferences value">{preference.value}</p>
          </Flex>
        ))}
      </Flex>
      {showIcons && (
        <Flex className="preferences suffix" align="center">
          <Icon iconName="tabler/chevron-right" onClick={onNextClicked} />
        </Flex>
      )}
    </Flex>
  );
};
